/**
 * Project caching system to prevent repeated database calls
 * Uses in-memory cache with fallback to sessionStorage
 */

import type { Project } from '../../../shared/lib/supabase';

interface CacheEntry {
  data: Project;
  timestamp: number;
  expiresAt: number;
}

interface ProjectCache {
  [projectId: string]: CacheEntry;
}

// In-memory cache
let cache: ProjectCache = {};

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
const SESSION_STORAGE_KEY = 'anthro_project_cache';

/**
 * Get cached project data
 */
export const getCachedProject = (projectId: string): Project | null => {
  try {
    // Check in-memory cache first
    const cacheEntry = cache[projectId];
    if (cacheEntry && Date.now() < cacheEntry.expiresAt) {
      if (import.meta.env.DEV) {
        console.log(`Project cache hit (memory): ${projectId}`);
      }
      return cacheEntry.data;
    }

    // Check sessionStorage cache
    const sessionCache = getSessionCache();
    const sessionEntry = sessionCache[projectId];
    if (sessionEntry && Date.now() < sessionEntry.expiresAt) {
      if (import.meta.env.DEV) {
        console.log(`Project cache hit (session): ${projectId}`);
      }
      // Update in-memory cache
      cache[projectId] = sessionEntry;
      return sessionEntry.data;
    }

    return null;
  } catch (error) {
    console.warn('Error reading from project cache:', error);
    return null;
  }
};

/**
 * Cache project data
 */
export const setCachedProject = (projectId: string, project: Project): void => {
  try {
    const now = Date.now();
    const cacheEntry: CacheEntry = {
      data: project,
      timestamp: now,
      expiresAt: now + CACHE_DURATION
    };

    // Update in-memory cache
    cache[projectId] = cacheEntry;

    // Update sessionStorage cache
    const sessionCache = getSessionCache();
    sessionCache[projectId] = cacheEntry;
    setSessionCache(sessionCache);

    if (import.meta.env.DEV) {
      console.log(`Project cached: ${projectId} (${project.name})`);
    }
  } catch (error) {
    console.warn('Error writing to project cache:', error);
  }
};

/**
 * Invalidate cached project data
 */
export const invalidateProjectCache = (projectId: string): void => {
  try {
    // Remove from in-memory cache
    delete cache[projectId];

    // Remove from sessionStorage cache
    const sessionCache = getSessionCache();
    delete sessionCache[projectId];
    setSessionCache(sessionCache);

    if (import.meta.env.DEV) {
      console.log(`Project cache invalidated: ${projectId}`);
    }
  } catch (error) {
    console.warn('Error invalidating project cache:', error);
  }
};

/**
 * Clear all cached project data
 */
export const clearProjectCache = (): void => {
  try {
    cache = {};
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    
    if (import.meta.env.DEV) {
      console.log('Project cache cleared');
    }
  } catch (error) {
    console.warn('Error clearing project cache:', error);
  }
};

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = (): { 
  inMemoryCount: number; 
  sessionCount: number; 
  oldestEntry?: string;
  newestEntry?: string;
} => {
  try {
    const inMemoryCount = Object.keys(cache).length;
    const sessionCache = getSessionCache();
    const sessionCount = Object.keys(sessionCache).length;

    // Find oldest and newest entries
    let oldestTimestamp = Infinity;
    let newestTimestamp = 0;
    let oldestEntry: string | undefined;
    let newestEntry: string | undefined;

    Object.entries(cache).forEach(([projectId, entry]) => {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestEntry = projectId;
      }
      if (entry.timestamp > newestTimestamp) {
        newestTimestamp = entry.timestamp;
        newestEntry = projectId;
      }
    });

    return {
      inMemoryCount,
      sessionCount,
      oldestEntry,
      newestEntry
    };
  } catch (error) {
    console.warn('Error getting cache stats:', error);
    return { inMemoryCount: 0, sessionCount: 0 };
  }
};

/**
 * Clean up expired cache entries
 */
export const cleanupExpiredCache = (): void => {
  try {
    const now = Date.now();
    let cleanedCount = 0;

    // Clean in-memory cache
    Object.keys(cache).forEach(projectId => {
      if (cache[projectId].expiresAt <= now) {
        delete cache[projectId];
        cleanedCount++;
      }
    });

    // Clean sessionStorage cache
    const sessionCache = getSessionCache();
    Object.keys(sessionCache).forEach(projectId => {
      if (sessionCache[projectId].expiresAt <= now) {
        delete sessionCache[projectId];
        cleanedCount++;
      }
    });
    setSessionCache(sessionCache);

    if (import.meta.env.DEV && cleanedCount > 0) {
      console.log(`Cleaned up ${cleanedCount} expired cache entries`);
    }
  } catch (error) {
    console.warn('Error cleaning up expired cache:', error);
  }
};

/**
 * Get sessionStorage cache
 */
function getSessionCache(): ProjectCache {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Error reading sessionStorage cache:', error);
    return {};
  }
}

/**
 * Set sessionStorage cache
 */
function setSessionCache(cache: ProjectCache): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Error writing sessionStorage cache:', error);
  }
}

// Auto-cleanup expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(cleanupExpiredCache, 5 * 60 * 1000);
}

// Development utilities
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__projectCache = {
    get: getCachedProject,
    set: setCachedProject,
    invalidate: invalidateProjectCache,
    clear: clearProjectCache,
    stats: getCacheStats,
    cleanup: cleanupExpiredCache
  };
}